// 📄 services/reporteProyectoService.ts
//
// Fase 1 de "reportes exportables" (2026-08-20, a pedido del usuario) —
// PDF de progreso general del proyecto: soluciones activas, % de
// configuración completado, incidentes abiertos/cerrados. Generado 100%
// en el cliente (jsPDF + jspdf-autotable) — sin Cloud Function nueva, sin
// deploy de nada, el botón "Exportar PDF" dispara la descarga directo en
// el navegador. Reusa `ProjectStats` (useProjectStats.ts) que la página
// "Soluciones del Proyecto" ya tiene cargado — solo se pide fresco el
// desglose de incidentes por estado (abiertos/cerrados), que el hook no
// expone tal cual.
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Project } from './projectService.ts';
import { ProjectStats } from '../hooks/useProjectStats.ts';
import { getIncidentes } from './incidenteService.ts';
import { SOLUCIONES } from '../pages/Proyectos/solucionesConfig.ts';

const AZUL = '#2563eb';
const GRIS_TEXTO = '#374151';
const GRIS_CLARO = '#9ca3af';

/**
 * Genera y descarga el PDF de progreso general — mismo criterio de
 * "abierto"/"cerrado" que ya usa el resto de la app (ver
 * useCasosPruebaFallidos y el filtro de incidentesAbiertos en
 * incidenteService.ts): Abierto/En progreso cuenta como abierto,
 * Resuelto/Cerrado cuenta como cerrado.
 */
export const generarReporteProgresoPDF = async (
  project: Project,
  stats: ProjectStats,
  ambienteActivo: string = 'DEV'
): Promise<void> => {
  const incidentes = await getIncidentes(project.id);
  const abiertos = incidentes.filter((i) => i.estado === 'Abierto' || i.estado === 'En progreso').length;
  const cerrados = incidentes.filter((i) => i.estado === 'Resuelto' || i.estado === 'Cerrado').length;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // ── Encabezado ──────────────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setTextColor(AZUL);
  doc.setFont('helvetica', 'bold');
  doc.text('Reporte de Progreso', 14, y);

  doc.setFontSize(10);
  doc.setTextColor(GRIS_CLARO);
  doc.setFont('helvetica', 'normal');
  doc.text('OneHub Platform', pageWidth - 14, y, { align: 'right' });

  y += 8;
  doc.setDrawColor(230, 230, 230);
  doc.line(14, y, pageWidth - 14, y);
  y += 10;

  doc.setFontSize(14);
  doc.setTextColor(GRIS_TEXTO);
  doc.setFont('helvetica', 'bold');
  doc.text(project.nombre, 14, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(GRIS_CLARO);
  doc.text(`Cliente: ${project.clienteNombre || 'Sin cliente'}`, 14, y);
  doc.text(`Generado: ${new Date().toLocaleDateString('es', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - 14, y, { align: 'right' });
  y += 12;

  // ── Progreso de configuración ───────────────────────────────────────
  doc.setFontSize(12);
  doc.setTextColor(GRIS_TEXTO);
  doc.setFont('helvetica', 'bold');
  doc.text(`Progreso de configuración (ambiente ${ambienteActivo})`, 14, y);
  y += 7;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`${stats.porcentaje}% completado — ${stats.pasosCompletados} de ${stats.totalPasos} pasos`, 14, y);
  y += 4;

  // Barra de progreso simple (rectángulo relleno proporcional al %).
  const barX = 14;
  const barWidth = pageWidth - 28;
  const barHeight = 4;
  doc.setFillColor(229, 231, 235);
  doc.rect(barX, y, barWidth, barHeight, 'F');
  doc.setFillColor(37, 99, 235);
  doc.rect(barX, y, barWidth * (stats.porcentaje / 100), barHeight, 'F');
  y += 12;

  // ── Métricas clave ───────────────────────────────────────────────────
  autoTable(doc, {
    startY: y,
    head: [['SR creadas', 'Issues abiertos', 'Interfaces activas', 'Incidentes abiertos', 'Incidentes cerrados']],
    body: [[String(stats.srCount), String(stats.issuesAbiertosCount), String(stats.interfacesCount), String(abiertos), String(cerrados)]],
    theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], fontSize: 9 },
    bodyStyles: { fontSize: 10, halign: 'center' },
    margin: { left: 14, right: 14 }
  });
  // @ts-ignore — jspdf-autotable amplía el doc en runtime con `lastAutoTable`.
  y = doc.lastAutoTable.finalY + 12;

  // ── Soluciones activas ───────────────────────────────────────────────
  const solucionesLabels = (project.solucionesActivas || [])
    .map((slug) => SOLUCIONES[slug]?.label || slug);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(GRIS_TEXTO);
  doc.text('Soluciones activas', 14, y);
  y += 7;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(solucionesLabels.length > 0 ? solucionesLabels.join(', ') : 'Sin soluciones activas', 14, y);
  y += 12;

  // ── Avance por pestaña ───────────────────────────────────────────────
  if (stats.avancePorTab.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Avance por pestaña', 14, y);
    y += 4;

    autoTable(doc, {
      startY: y + 3,
      head: [['Pestaña', 'Completados', 'Total', '%']],
      body: stats.avancePorTab.map((t) => [
        t.tab,
        String(t.completados),
        String(t.total),
        t.total > 0 ? `${Math.round((t.completados / t.total) * 100)}%` : '—'
      ]),
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235], fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      margin: { left: 14, right: 14 }
    });
  }

  const nombreArchivo = `Reporte-${project.nombre.replace(/[^a-zA-Z0-9]/g, '_')}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(nombreArchivo);
};
